import { gql } from 'apollo-angular';

import { ERROR_RESULT_FRAGMENT } from './shared-definitions';

export const DISCOUNT_FRAGMENT = gql`
  fragment Discount on Discount {
    adjustmentSource
    amount
    amountWithTax
    description
    type
  }
`;

export const REFUND_FRAGMENT = gql`
  fragment Refund on Refund {
    id
    state
    items
    shipping
    adjustment
    transactionId
    paymentId
  }
`;

export const ORDER_ADDRESS_FRAGMENT = gql`
  fragment OrderAddress on OrderAddress {
    fullName
    company
    streetLine1
    streetLine2
    city
    province
    postalCode
    country
    countryCode
    phoneNumber
  }
`;

export const ORDER_FRAGMENT = gql`
  fragment Order on Order {
    id
    createdAt
    updatedAt
    orderPlacedAt
    code
    state
    nextStates
    total
    currencyCode
    customer {
      id
      firstName
      lastName
    }
    shippingLines {
      shippingMethod {
        name
      }
    }
  }
`;

export const FULFILLMENT_FRAGMENT = gql`
  fragment Fulfillment on Fulfillment {
    id
    state
    nextStates
    createdAt
    updatedAt
    method
    orderItems {
      id
    }
    trackingCode
    uploadedFiles {
      idFileUrl
      signatureFileUrl
    }
  }
`;

export const ORDER_LINE_CUSTOM_FRAGMENT = gql`
  fragment OrderLine on OrderLineNew {
    id
    featuredAsset {
      preview
    }
    productVariant {
      id
      name
      sku
      trackInventory
      stockOnHand
      product {
        name
        translations {
          name
        }
        customFields {
          externalId
        }
      }
    }
    discounts {
      ...Discount
    }
    unitPrice
    unitPriceWithTax
    proratedUnitPrice
    proratedUnitPriceWithTax
    quantity
    items {
      id
      unitPrice
      unitPriceWithTax
      taxRate
      refundId
      cancelled
      discountedUnitPriceWithTax
      fulfillment {
        ...Fulfillment
      }
    }
    linePrice
    lineTax
    linePriceWithTax
    discountedLinePrice
    discountedLinePriceWithTax
    customFields
  }
`;

export const ORDER_LINE_FRAGMENT = gql`
  fragment OrderLine on OrderLine {
    id
    featuredAsset {
      preview
    }
    productVariant {
      id
      name
      sku
      trackInventory
      stockOnHand
      product {
        name
        customFields {
          externalId
        }
      }
    }
    discounts {
      ...Discount
    }
    unitPrice
    unitPriceWithTax
    proratedUnitPrice
    proratedUnitPriceWithTax
    quantity
    items {
      id
      unitPrice
      unitPriceWithTax
      taxRate
      refundId
      cancelled
      discountedUnitPriceWithTax
      fulfillment {
        ...Fulfillment
      }
    }
    linePrice
    lineTax
    linePriceWithTax
    discountedLinePrice
    discountedLinePriceWithTax
  }
`;

export const ORDER_DETAIL_CUSTOM_FRAGMENT = gql`
  fragment OrderDetail on orderNew {
    id
    createdAt
    updatedAt
    orderPlacedAt
    code
    state
    nextStates
    active
    customer {
      id
      firstName
      lastName
    }
    lines {
      ...OrderLine
    }
    surcharges {
      id
      sku
      description
      price
      priceWithTax
      taxRate
    }
    discounts {
      ...Discount
    }
    promotions {
      id
      couponCode
    }
    subTotal
    subTotalWithTax
    total
    totalWithTax
    currencyCode
    shipping
    shippingWithTax
    shippingLines {
      shippingMethod {
        id
        code
        name
        fulfillmentHandlerCode
        description
      }
    }
    taxSummary {
      description
      taxBase
      taxRate
      taxTotal
    }
    shippingAddress {
      ...OrderAddress
    }
    billingAddress {
      ...OrderAddress
    }
    payments {
      id
      createdAt
      transactionId
      amount
      method
      state
      nextStates
      errorMessage
      metadata
      refunds {
        id
        createdAt
        state
        items
        adjustment
        total
        paymentId
        reason
        transactionId
        method
        metadata
        orderItems {
          id
        }
      }
    }
    fulfillments {
      ...Fulfillment
    }
    modifications {
      id
      createdAt
      isSettled
      priceChange
      note
      payment {
        id
        amount
      }
      orderItems {
        id
      }
      refund {
        id
        paymentId
        total
      }
      surcharges {
        id
      }
    }
    customFields {
      assignedOperator {
        id
      }
      selectedCurrency
      currencyRate
      syncedToLS
      specialDeliveryCost
      specialDeliveryDaysFrom
      specialDeliveryDaysTo
    }
  }
  ${DISCOUNT_FRAGMENT}
  ${ORDER_ADDRESS_FRAGMENT}
  ${FULFILLMENT_FRAGMENT}
  ${ORDER_LINE_CUSTOM_FRAGMENT}
`;

export const ORDER_DETAIL_FRAGMENT = gql`
  fragment OrderDetail on Order {
    id
    createdAt
    updatedAt
    code
    state
    nextStates
    active
    customer {
      id
      firstName
      lastName
    }
    lines {
      ...OrderLine
    }
    surcharges {
      id
      sku
      description
      price
      priceWithTax
      taxRate
    }
    discounts {
      ...Discount
    }
    promotions {
      id
      couponCode
    }
    subTotal
    subTotalWithTax
    total
    totalWithTax
    currencyCode
    shipping
    shippingWithTax
    shippingLines {
      shippingMethod {
        id
        code
        name
        fulfillmentHandlerCode
        description
      }
    }
    taxSummary {
      description
      taxBase
      taxRate
      taxTotal
    }
    shippingAddress {
      ...OrderAddress
    }
    billingAddress {
      ...OrderAddress
    }
    payments {
      id
      createdAt
      transactionId
      amount
      method
      state
      nextStates
      errorMessage
      metadata
      refunds {
        id
        createdAt
        state
        items
        adjustment
        total
        paymentId
        reason
        transactionId
        method
        metadata
        orderItems {
          id
        }
      }
    }
    fulfillments {
      ...Fulfillment
    }
    modifications {
      id
      createdAt
      isSettled
      priceChange
      note
      payment {
        id
        amount
      }
      orderItems {
        id
      }
      refund {
        id
        paymentId
        total
      }
      surcharges {
        id
      }
    }
  }
  ${DISCOUNT_FRAGMENT}
  ${ORDER_ADDRESS_FRAGMENT}
  ${FULFILLMENT_FRAGMENT}
  ${ORDER_LINE_FRAGMENT}
`;

export const GET_ORDERS_LIST = gql`
  query GetOrderList($options: OrderListOptions) {
    orders(options: $options) {
      items {
        ...Order
      }
      totalItems
    }
  }
  ${ORDER_FRAGMENT}
`;

export const GET_ORDER = gql`
  query GetOrder($id: ID!) {
    orderDetails(id: $id) {
      ...OrderDetail
    }
  }
  ${ORDER_DETAIL_CUSTOM_FRAGMENT}
`;

export const SETTLE_PAYMENT = gql`
  mutation SettlePayment($id: ID!) {
    settlePayment(id: $id) {
      ... on Payment {
        id
        transactionId
        amount
        method
        state
        metadata
      }
      ...ErrorResult
      ... on SettlePaymentError {
        paymentErrorMessage
      }
      ... on PaymentStateTransitionError {
        transitionError
      }
      ... on OrderStateTransitionError {
        transitionError
      }
    }
  }
  ${ERROR_RESULT_FRAGMENT}
`;

export const TRANSITION_PAYMENT_TO_STATE = gql`
  mutation TransitionPaymentToState($id: ID!, $state: String!) {
    transitionPaymentToState(id: $id, state: $state) {
      ... on Payment {
        id
        transactionId
        amount
        method
        state
        metadata
      }
      ...ErrorResult
      ... on PaymentStateTransitionError {
        transitionError
      }
    }
  }
  ${ERROR_RESULT_FRAGMENT}
`;

export const CREATE_FULFILLMENT = gql`
  mutation CreateFulfillment($input: FulfillOrderInputCustom!) {
    addFulfillmentToOrderCustom(input: $input) {
      ...Fulfillment
    }
  }
  ${FULFILLMENT_FRAGMENT}
`;

// NOTE original
// export const CREATE_FULFILLMENT = gql`
//   mutation CreateFulfillment($input: FulfillOrderInputCustom!) {
//     addFulfillmentToOrderCustom(input: $input) {
//       ...Fulfillment
//       ... on CreateFulfillmentError {
//         errorCode
//         message
//         fulfillmentHandlerError
//       }
//       ... on FulfillmentStateTransitionError {
//         errorCode
//         message
//         transitionError
//       }
//       ...ErrorResult
//     }
//   }
//   ${FULFILLMENT_FRAGMENT}
//   ${ERROR_RESULT_FRAGMENT}
// `;

export const CANCEL_ORDER = gql`
  mutation CancelOrder($input: CancelOrderInput!) {
    cancelOrder(input: $input) {
      ...OrderDetail
      ...ErrorResult
    }
  }
  ${ORDER_DETAIL_FRAGMENT}
  ${ERROR_RESULT_FRAGMENT}
`;

export const REFUND_ORDER = gql`
  mutation RefundOrder($input: RefundOrderInput!) {
    refundOrder(input: $input) {
      ...Refund
      ...ErrorResult
    }
  }
  ${REFUND_FRAGMENT}
  ${ERROR_RESULT_FRAGMENT}
`;

export const SETTLE_REFUND = gql`
  mutation SettleRefund($input: SettleRefundInput!) {
    settleRefund(input: $input) {
      ...Refund
      ...ErrorResult
    }
  }
  ${REFUND_FRAGMENT}
  ${ERROR_RESULT_FRAGMENT}
`;

export const GET_ORDER_HISTORY = gql`
  query GetOrderHistory($id: ID!, $options: HistoryEntryListOptions) {
    order(id: $id) {
      id
      history(options: $options) {
        totalItems
        items {
          id
          type
          createdAt
          isPublic
          administrator {
            id
            firstName
            lastName
          }
          data
        }
      }
    }
  }
`;

export const ADD_NOTE_TO_ORDER = gql`
  mutation AddNoteToOrder($input: AddNoteToOrderInput!) {
    addNoteToOrder(input: $input) {
      id
    }
  }
`;

export const UPDATE_ORDER_NOTE = gql`
  mutation UpdateOrderNote($input: UpdateOrderNoteInput!) {
    updateOrderNote(input: $input) {
      id
      data
      isPublic
    }
  }
`;

export const DELETE_ORDER_NOTE = gql`
  mutation DeleteOrderNote($id: ID!) {
    deleteOrderNote(id: $id) {
      result
      message
    }
  }
`;

export const TRANSITION_ORDER_TO_STATE = gql`
  mutation transitionOrderToStateAdmin($id: ID!, $state: String!, $force: Boolean) {
    transitionOrderToStateAdmin(id: $id, state: $state, force: $force) {
      ...Order
      ...ErrorResult
      ... on OrderStateTransitionError {
        transitionError
      }
    }
  }
  ${ORDER_FRAGMENT}
  ${ERROR_RESULT_FRAGMENT}
`;

export const UPDATE_ORDER_CUSTOM_FIELDS = gql`
  mutation UpdateOrderCustomFields($input: UpdateOrderInput!) {
    setOrderCustomFields(input: $input) {
      ...Order
    }
  }
  ${ORDER_FRAGMENT}
`;

export const TRANSITION_FULFILLMENT_TO_STATE = gql`
  mutation TransitionFulfillmentToState($id: ID!, $state: String!) {
    transitionFulfillmentToState(id: $id, state: $state) {
      ...Fulfillment
      ...ErrorResult
      ... on FulfillmentStateTransitionError {
        transitionError
      }
    }
  }
  ${FULFILLMENT_FRAGMENT}
  ${ERROR_RESULT_FRAGMENT}
`;

export const GET_ORDER_SUMMARY = gql`
  query GetOrderSummary($start: DateTime!, $end: DateTime!) {
    orders(options: { filter: { orderPlacedAt: { between: { start: $start, end: $end } } } }) {
      totalItems
      items {
        id
        total
        currencyCode
      }
    }
  }
`;

export const MODIFY_ORDER = gql`
  mutation ModifyOrder($input: ModifyOrderInput!) {
    modifyOrder(input: $input) {
      ...OrderDetail
      ...ErrorResult
    }
  }
  ${ORDER_DETAIL_FRAGMENT}
  ${ERROR_RESULT_FRAGMENT}
`;

export const ADD_MANUAL_PAYMENT_TO_ORDER = gql`
  mutation AddManualPayment($input: ManualPaymentInput!) {
    addManualPaymentToOrder(input: $input) {
      ...OrderDetail
      ...ErrorResult
    }
  }
  ${ORDER_DETAIL_FRAGMENT}
  ${ERROR_RESULT_FRAGMENT}
`;
